import { ApplicationState, RootState } from '@/store/storeStateInterfaces';
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import ApplicationRepository from '@/api/repositories/ApplicationRepository';
import { RepositoryFactory } from '@/api/RepositoryFactory';
import Theme from '@/models/Theme';

const store: ApplicationState = {
    noAnimations: process.env.VUE_APP_DEFAULT_NO_ANIMATIONS_STATE === 'true',
    categoryMenu: false,
    languageDialog: false,
    locationDialog: false,
    termsAndConditionsDialog: false,
    termsAndConditionsConfirmed: null,
    invalidTokenDialog: false,
    accessibilityControls: null,
    usingCode: false,
    navigationTitle: '',
    customTheme: undefined,
    inactivityDialog: false,
    inactivityConfirmed: false
};

const applicationRepository: ApplicationRepository = RepositoryFactory.get('application');

// -- timeout vars --
const inactivityTimeout = Number(process.env!.VUE_APP_INACTIVITY_TIMEOUT) * 60 * 1000;
let inactivityTimerId: NodeJS.Timeout | undefined;

const inactivityDialogTimeout = Number(process.env!.VUE_APP_INACTIVITY_POPUP_TIMEOUT) * 60 * 1000;
let inactivityDialogTimerId: NodeJS.Timeout | undefined;

const actions: ActionTree<ApplicationState, RootState> = {
    loadThemeAction: async ({ commit }, themeToken: string): Promise<Theme> => {
        const response = await applicationRepository.loadTheme(themeToken);
        const customTheme = new Theme(response.data.response);
        commit('setCustomTheme', customTheme);
        return customTheme;
    },
    setInactivityTimer: ({ commit, dispatch }): void => {
        dispatch('clearInactivityTimer');
        inactivityTimerId = setTimeout(() => {
            commit('setInactivityDialog', true);
            dispatch('setInactivityDialogTimer');
        }, inactivityTimeout);
    },
    setInactivityDialogTimer: ({ commit, dispatch }): void => {
        inactivityDialogTimerId = setTimeout(() => {
            commit('setInactivityConfirmed', true);
        }, inactivityDialogTimeout);
    },
    clearInactivityTimer: ({ commit, getters }): void => {
        commit('setInactivityDialog', false);
        commit('setInactivityConfirmed', false);
        if (inactivityTimerId) {
            clearTimeout(inactivityTimerId);
            inactivityTimerId = undefined;
        }
        if (inactivityDialogTimerId) {
            clearTimeout(inactivityDialogTimerId);
            inactivityDialogTimerId = undefined;
        }
    }
};

const mutations: MutationTree<ApplicationState> = {
    setNoAnimations: (state: ApplicationState, value: boolean) => {
        state.noAnimations = value;
    },
    setCategoryMenu: (state: ApplicationState, value: boolean) => {
        state.categoryMenu = value;
    },
    setLanguageDialog: (state: ApplicationState, value: boolean) => {
        state.languageDialog = value;
    },
    setLocationDialog: (state: ApplicationState, value: boolean) => {
        state.locationDialog = value;
    },
    setInvalidTokenDialog: (state: ApplicationState, value: boolean) => {
        state.invalidTokenDialog = value;
    },
    setInactivityDialog: (state: ApplicationState, value: boolean) => {
        state.inactivityDialog = value;
    },
    setInactivityConfirmed: (state: ApplicationState, value: boolean) => {
        state.inactivityConfirmed = value;
    },
    setTermsAndConditionsConfirmed: (state: ApplicationState, value: boolean) => {
        state.termsAndConditionsConfirmed = value;
    },
    setTermsAndConditionsDialog: (state: ApplicationState, value: boolean) => {
        state.termsAndConditionsDialog = value;
    },
    setNavigationTitle: (state: ApplicationState, payload: { title: string }) => {
        state.navigationTitle = payload.title;
    },
    setAccessibilityControls: (state: ApplicationState, accessibilityControls: boolean) => {
        state.accessibilityControls = accessibilityControls;
    },
    setUsingCode: (state: ApplicationState, value: boolean) => {
        state.usingCode = value;
    },
    setCustomTheme: (state: ApplicationState, customTheme: Theme) => {
        state.customTheme = customTheme;
    }
};

const getters: GetterTree<ApplicationState, RootState> = {
    noAnimations: (state: ApplicationState) => state.noAnimations,
    categoryMenu: (state: ApplicationState) => state.categoryMenu,
    languageDialog: (state: ApplicationState) => state.languageDialog,
    locationDialog: (state: ApplicationState) => state.locationDialog,
    invalidTokenDialog: (state: ApplicationState) => state.invalidTokenDialog,
    inactivityDialog: (state: ApplicationState) => state.inactivityDialog,
    inactivityConfirmed: (state: ApplicationState) => state.inactivityConfirmed,
    termsAndConditionsDialog: (state: ApplicationState) => state.termsAndConditionsDialog,
    accessibilityControls: (state: ApplicationState) => state.accessibilityControls,
    usingCode: (state: ApplicationState) => state.usingCode,
    navigationTitle: (state: ApplicationState) => state.navigationTitle,
    customTheme: (state: ApplicationState) => state.customTheme,
    termsAndConditionsConfirmed: (state: ApplicationState) => state.termsAndConditionsConfirmed
};

const applicationStore: Module<ApplicationState, RootState> = {
    state: store,
    actions,
    mutations,
    getters
};

export default applicationStore;
